import React, { useMemo } from 'react';

// Griglia permessi macro → gruppo → voce, condivisa fra la scheda permessi di
// un utente e quella di un profilo: le due devono restare identiche, altrimenti
// diventa impossibile confrontare a colpo d'occhio cosa eredita e cosa deroga.

export type CampoPermesso = 'canView' | 'canEdit' | 'canDelete' | 'canExport';

export interface FlagsPermesso {
  canView:   boolean;
  canEdit:   boolean;
  canDelete: boolean;
  canExport: boolean;
}

export const TUTTO_ON:  FlagsPermesso = { canView: true,  canEdit: true,  canDelete: true,  canExport: true  };
export const SOLA_LETT: FlagsPermesso = { canView: true,  canEdit: false, canDelete: false, canExport: false };
export const TUTTO_OFF: FlagsPermesso = { canView: false, canEdit: false, canDelete: false, canExport: false };

export type OriginePermesso = 'profilo' | 'eccezione' | 'nessuno';

export interface VoceGriglia extends FlagsPermesso {
  modulo:  string;
  label:   string;
  macro:   string;
  gruppo:  string;
  dirty:   boolean;
  /** Presente solo nella scheda utente: da dove arriva il valore mostrato. */
  origine?: OriginePermesso;
  /** Valori che il profilo imporrebbe, per il pulsante "ripristina". */
  profilo?: FlagsPermesso | null;
}

interface Props {
  voci: VoceGriglia[];
  onToggle: (modulo: string, campo: CampoPermesso) => void;
  onApplicaGruppo: (filtro: (v: VoceGriglia) => boolean, flags: FlagsPermesso) => void;
  /** Mostra la colonna origine e il pulsante ripristina (solo scheda utente). */
  mostraEredita?: boolean;
  onRipristina?: (modulo: string) => void;
  disabilitata?: boolean;
}

export default function GrigliaPermessi({
  voci, onToggle, onApplicaGruppo, mostraEredita = false, onRipristina, disabilitata = false,
}: Props) {

  // Raggruppa la lista piatta in macro → gruppo, preservando l'ordine di arrivo
  // (che è quello di moduliPermessi.js, speculare alla sidebar).
  const macroGruppi = useMemo(() => {
    const ordine: string[] = [];
    const mappa: Record<string, { macro: string; gruppi: { gruppo: string; voci: VoceGriglia[] }[] }> = {};
    const idxGruppo: Record<string, Record<string, number>> = {};

    for (const v of voci) {
      if (!mappa[v.macro]) {
        mappa[v.macro] = { macro: v.macro, gruppi: [] };
        idxGruppo[v.macro] = {};
        ordine.push(v.macro);
      }
      const entry = mappa[v.macro];
      if (idxGruppo[v.macro][v.gruppo] === undefined) {
        idxGruppo[v.macro][v.gruppo] = entry.gruppi.length;
        entry.gruppi.push({ gruppo: v.gruppo, voci: [] });
      }
      entry.gruppi[idxGruppo[v.macro][v.gruppo]].voci.push(v);
    }

    return ordine.map(m => mappa[m]);
  }, [voci]);

  const renderAzioni = (filtro: (v: VoceGriglia) => boolean) => (
    <div className="impostazioni-permessi-sezione-actions">
      <button className="bop-btn bop-btn-ghost bop-btn-sm" disabled={disabilitata}
        onClick={() => onApplicaGruppo(filtro, TUTTO_ON)}>
        Tutto
      </button>
      <button className="bop-btn bop-btn-ghost bop-btn-sm" disabled={disabilitata}
        onClick={() => onApplicaGruppo(filtro, SOLA_LETT)}>
        Solo lettura
      </button>
      <button className="bop-btn bop-btn-ghost bop-btn-sm impostazioni-permessi-btn-danger" disabled={disabilitata}
        onClick={() => onApplicaGruppo(filtro, TUTTO_OFF)}>
        Niente
      </button>
    </div>
  );

  const renderOrigine = (v: VoceGriglia) => {
    if (v.dirty) return <span className="impostazioni-permessi-tag-dirty">modificato</span>;
    if (v.origine === 'eccezione') {
      return (
        <>
          <span className="impostazioni-permessi-origine impostazioni-permessi-origine--eccezione">eccezione</span>
          {onRipristina && (
            <button
              className="bop-btn bop-btn-ghost bop-btn-sm impostazioni-permessi-btn-ripristina"
              disabled={disabilitata}
              onClick={() => onRipristina(v.modulo)}
              title="Riporta questa voce al valore del profilo"
            >
              ripristina
            </button>
          )}
        </>
      );
    }
    if (v.origine === 'profilo') {
      return <span className="impostazioni-permessi-origine impostazioni-permessi-origine--profilo">da profilo</span>;
    }
    return null;
  };

  const campi: { campo: CampoPermesso; classe: string }[] = [
    { campo: 'canView',   classe: 'impostazioni-permessi-checkbox-view' },
    { campo: 'canEdit',   classe: 'impostazioni-permessi-checkbox-edit' },
    { campo: 'canDelete', classe: 'impostazioni-permessi-checkbox-delete' },
    { campo: 'canExport', classe: 'impostazioni-permessi-checkbox-export' },
  ];

  return (
    <>
      {macroGruppi.map(macro => (
        <div className="bop-card impostazioni-permessi-macro-card" key={macro.macro}>
          <div className="impostazioni-permessi-macro-header">
            <h3 className="impostazioni-permessi-macro-title">{macro.macro}</h3>
            {renderAzioni(v => v.macro === macro.macro)}
          </div>

          {macro.gruppi.map(gruppo => (
            <div className="impostazioni-permessi-gruppo-block" key={`${macro.macro}|${gruppo.gruppo}`}>
              <div className="impostazioni-permessi-gruppo-header">
                <h4 className="impostazioni-permessi-gruppo-title">{gruppo.gruppo}</h4>
                {renderAzioni(v => v.macro === macro.macro && v.gruppo === gruppo.gruppo)}
              </div>

              <div className="bop-table-wrapper">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Voce</th>
                      <th className="impostazioni-permessi-th-center">View</th>
                      <th className="impostazioni-permessi-th-center">Edit</th>
                      <th className="impostazioni-permessi-th-center">Delete</th>
                      <th className="impostazioni-permessi-th-center">Export</th>
                      <th className={mostraEredita
                        ? 'impostazioni-permessi-th-origine'
                        : 'impostazioni-permessi-th-stato'} />
                    </tr>
                  </thead>
                  <tbody>
                    {gruppo.voci.map(v => {
                      const classi = [
                        v.dirty ? 'impostazioni-permessi-row-dirty' : '',
                        mostraEredita && v.origine === 'eccezione' ? 'impostazioni-permessi-row-eccezione' : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <tr key={v.modulo} className={classi}>
                          <td className="impostazioni-permessi-td-label">{v.label}</td>
                          {campi.map(({ campo, classe }) => (
                            <td className="impostazioni-permessi-td-center" key={campo}>
                              <input
                                type="checkbox"
                                checked={v[campo]}
                                onChange={() => onToggle(v.modulo, campo)}
                                disabled={disabilitata || (campo !== 'canView' && !v.canView)}
                                className={`impostazioni-permessi-checkbox ${classe}`}
                              />
                            </td>
                          ))}
                          <td className={mostraEredita
                            ? 'impostazioni-permessi-td-origine'
                            : undefined}>
                            {mostraEredita
                              ? renderOrigine(v)
                              : (v.dirty && <span className="impostazioni-permessi-tag-dirty">modificato</span>)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * Applica le regole di coerenza fra i flag, identiche a quelle del backend
 * (normalizzaFlags in utils/permessi.js): edit/delete/export implicano view,
 * senza view gli altri si spengono.
 */
export function normalizzaFlags(f: FlagsPermesso): FlagsPermesso {
  const canEdit   = !!f.canEdit;
  const canDelete = !!f.canDelete;
  const canExport = !!f.canExport;
  const canView   = (canEdit || canDelete || canExport) ? true : !!f.canView;
  if (!canView) return { ...TUTTO_OFF };
  return { canView, canEdit, canDelete, canExport };
}

/** True se due set di flag coincidono. */
export function flagsUguali(a: FlagsPermesso, b: FlagsPermesso): boolean {
  return a.canView === b.canView && a.canEdit === b.canEdit
      && a.canDelete === b.canDelete && a.canExport === b.canExport;
}
